import { useEffect, useState } from "react";
import { adminEndpoints } from "@/lib/adminApi";
import { AdminPageHeader, AdminCard, EmptyState } from "@/components/admin/AdminUI";
import { X } from "lucide-react";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function AdminTraineesPage() {
    const [trainees, setTrainees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDetail, setSelectedDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        adminEndpoints
            .trainees()
            .then((d) => {
                if (!cancelled) setTrainees(d);
            })
            .catch((err) => {
                if (!cancelled) setError(err?.response?.data?.detail || "Failed to load trainees");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const openDetail = async (traineeId) => {
        setDetailLoading(true);
        setSelectedDetail({ id: traineeId, placeholder: true });
        try {
            const d = await adminEndpoints.trainee(traineeId);
            setSelectedDetail(d);
        } catch (err) {
            setSelectedDetail(null);
        } finally {
            setDetailLoading(false);
        }
    };

    return (
        <div data-testid="admin-trainees-page">
            <AdminPageHeader
                eyebrow="Portal users"
                title="Trainees"
                description="Registered trainees, their enrollments and issued certificates."
                testId="admin-trainees-header"
            />

            {error && (
                <p className="mb-6 border border-rose-400/40 bg-rose-500/10 px-4 py-3 font-mono text-xs text-rose-200">
                    {error}
                </p>
            )}

            {loading ? (
                <AdminCard>Loading…</AdminCard>
            ) : trainees.length === 0 ? (
                <EmptyState
                    title="No trainees yet"
                    testId="admin-trainees-empty"
                />
            ) : (
                <div className="overflow-x-auto border border-white/10 bg-white/[0.02]">
                    <table className="min-w-full text-left text-sm" data-testid="admin-trainees-table">
                        <thead className="border-b border-white/10 bg-white/[0.03] font-mono text-[10px] uppercase tracking-[0.2em] text-brand-offwhite/60">
                            <tr>
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Company</th>
                                <th className="px-4 py-3">Signup</th>
                                <th className="px-4 py-3 text-center">Enrolled</th>
                                <th className="px-4 py-3 text-center">Certs</th>
                            </tr>
                        </thead>
                        <tbody>
                            {trainees.map((t) => (
                                <tr
                                    key={t.id}
                                    onClick={() => openDetail(t.id)}
                                    className="cursor-pointer border-b border-white/5 last:border-0 hover:bg-white/[0.04]"
                                    data-testid={`admin-trainee-row-${t.id}`}
                                >
                                    <td className="px-4 py-3 font-body text-brand-offwhite">{t.full_name}</td>
                                    <td className="px-4 py-3 font-mono text-xs text-brand-offwhite/80">{t.email}</td>
                                    <td className="px-4 py-3 font-body text-brand-offwhite/80">
                                        {t.company || <span className="text-brand-offwhite/40">—</span>}
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs text-brand-offwhite/60">
                                        {formatDate(t.created_at)}
                                    </td>
                                    <td className="px-4 py-3 text-center font-display text-xl leading-none text-brand-offwhite">
                                        {t.enrolled_count}
                                    </td>
                                    <td className="px-4 py-3 text-center font-display text-xl leading-none text-brand-gold">
                                        {t.completed_count}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selectedDetail && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setSelectedDetail(null)}
                    data-testid="admin-trainee-drawer-backdrop"
                >
                    <div
                        className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto border border-white/10 bg-brand-charcoal p-8"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="admin-trainee-drawer"
                    >
                        <button
                            type="button"
                            onClick={() => setSelectedDetail(null)}
                            className="absolute right-4 top-4 grid h-8 w-8 place-items-center border border-white/20 hover:border-brand-gold"
                            data-testid="admin-trainee-drawer-close"
                        >
                            <X className="h-4 w-4" />
                        </button>
                        {detailLoading || selectedDetail.placeholder ? (
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand-offwhite/60">
                                Loading trainee…
                            </p>
                        ) : (
                            <>
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-gold">
                                    Trainee record
                                </p>
                                <h3 className="mt-2 font-display text-3xl uppercase tracking-wide">
                                    {selectedDetail.full_name}
                                </h3>
                                <p className="mt-1 font-mono text-sm text-brand-offwhite/80">
                                    {selectedDetail.email}
                                </p>
                                {selectedDetail.company && (
                                    <p className="mt-1 font-body text-sm text-brand-offwhite/70">
                                        {selectedDetail.company}
                                    </p>
                                )}
                                <div className="mt-6">
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/60">
                                        Enrollments ({selectedDetail.enrollments.length})
                                    </p>
                                    <ul className="mt-2 space-y-2">
                                        {selectedDetail.enrollments.length === 0 && (
                                            <li className="font-body text-sm text-brand-offwhite/50">
                                                No enrollments.
                                            </li>
                                        )}
                                        {selectedDetail.enrollments.map((e) => (
                                            <li
                                                key={e.course_id}
                                                className="border border-white/10 bg-white/[0.03] p-3"
                                                data-testid={`trainee-enrollment-${e.course_id}`}
                                            >
                                                <p className="font-body text-sm text-brand-offwhite">
                                                    {e.course_title}
                                                </p>
                                                <div className="mt-2 flex items-center gap-3">
                                                    <div className="h-1.5 flex-1 bg-white/10">
                                                        <div
                                                            className="h-full bg-brand-gold transition-all"
                                                            style={{ width: `${e.progress_percent}%` }}
                                                        />
                                                    </div>
                                                    <span className="font-mono text-[10px] text-brand-gold">
                                                        {e.progress_percent}%
                                                    </span>
                                                    <span className="font-mono text-[10px] text-brand-offwhite/50">
                                                        {e.completed_lessons}/{e.total_lessons}
                                                    </span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="mt-6">
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/60">
                                        Certificates ({selectedDetail.certificates.length})
                                    </p>
                                    <ul className="mt-2 space-y-2">
                                        {selectedDetail.certificates.length === 0 && (
                                            <li className="font-body text-sm text-brand-offwhite/50">
                                                None issued yet.
                                            </li>
                                        )}
                                        {selectedDetail.certificates.map((c) => (
                                            <li
                                                key={c.id}
                                                className="flex items-center justify-between border border-white/10 bg-white/[0.03] p-3"
                                                data-testid={`trainee-cert-${c.id}`}
                                            >
                                                <div>
                                                    <p className="font-body text-sm text-brand-offwhite">
                                                        {c.course_title}
                                                    </p>
                                                    <p className="font-mono text-xs text-brand-offwhite/60">
                                                        {c.cert_number} · issued {formatDate(c.issued_at)}
                                                    </p>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
